/**
 * Labels for a worker's budgeted hours on one engagement, when scheduled changes make them vary over time.
 */
import type { BudgetedHoursTimeline } from '@/types/ui/resource-availability'

/** The hours in effect on `date` (ISO yyyy-MM-dd). */
export const hoursOn = (timeline: BudgetedHoursTimeline, date: string): number | null => {
  let hours = timeline.baseline
  for (const change of timeline.changes) {
    if (change.effectiveDate > date) break
    hours = change.hoursPerDay
  }
  return hours
}

/**
 * The chip text for a worker's budgeted hours across their time on an engagement: "8h/day" when it holds
 * steady, "8→4h/day" when scheduled changes move it, each value in the order it takes effect. Without a
 * timeline, the flat value; without either, nothing.
 */
export const budgetedHoursLabel = (
  current: number | undefined,
  timeline: BudgetedHoursTimeline | undefined,
  from: string,
  to: string
): string | undefined => {
  if (!timeline) {
    return current !== undefined ? `${current}h/day` : undefined
  }

  const values: number[] = []
  const push = (value: number | null) => {
    if (value !== null && values[values.length - 1] !== value) values.push(value)
  }

  push(hoursOn(timeline, from))
  for (const change of timeline.changes) {
    if (change.effectiveDate > from && change.effectiveDate <= to) push(change.hoursPerDay)
  }

  return values.length > 0 ? `${values.join('→')}h/day` : undefined
}
